import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { Link, useParams } from 'react-router-dom';
import { Loader } from 'react-bootstrap-typeahead';
import Button from 'components/Button';
import { useApiGet, useApiPut } from 'hooks/useApi';
import { AdminSupportContact } from 'models/AdminSupportContact';
import moment from 'moment';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import Pagination from 'models/Pagination';
import AdminUser from 'models/AdminUser';
import { useToast } from 'hooks/useToast';
import ToastType from 'enum/ToastType';

type AssignPayload = {
  atendenteId?: string | null;
};

const AdminSupportContactsFormPage = () => {
  const params = useParams();
  const { addToast } = useToast();

  const contactId = params.id;

  const { data: contact, isLoading: isFetching, refetch } = useApiGet<
    AdminSupportContact
  >('/admin/support/contacts', {
    id: contactId,
    enabled: !!contactId
  });

  const { mutate: assignMutate, isPending: isAssigning } = useApiPut<
    AdminSupportContact
  >('/admin/support/contacts', contactId ? `${contactId}/assign` : undefined);

  const [usersSearch, setUsersSearch] = useState('');
  const [openUserPanel, setOpenUserPanel] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: users, isLoading: isLoadingUsers } = useApiGet<
    Pagination<AdminUser>
  >(
    '/admin/auth/users',
    usersSearch
      ? {
          params: { search: usersSearch }
        }
      : undefined
  );

  useEffect(() => {
    if (contact?.atendente?.id) {
      setSelectedUserId(contact.atendente.id);
    }
  }, [contact?.atendente?.id]);

  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    const items = users?.items || [];
    return items.find((u) => u.id === selectedUserId) || null;
  }, [selectedUserId, users?.items]);

  const handleAssign = useCallback(
    (payload: AssignPayload) => {
      if (!contactId) return;
      assignMutate(payload as any, {
        onError: (response: any) => {
          addToast(
            ToastType.ERROR,
            response.errors?.detail?.toString() || 'Erro ao atribuir atendente'
          );
        },
        onSuccess: async () => {
          addToast(ToastType.SUCCESS, 'Atendente atribuído com sucesso');
          await refetch();
        }
      });
    },
    [addToast, assignMutate, contactId, refetch]
  );

  return (
    <div className="padding-page">
      <Row className="d-flex flex-column flex-md-row">
        <Col>
          <p className="text-gray-600 mb-1">
            <Link
              to="/admin/support/contacts"
              className="text-gray-600 text-decoration-none"
            >
              Contatos de Suporte
            </Link>
            {' / '}
            Detalhes
          </p>
          <h4 className="fw-bold mb-1">Detalhes do Contato</h4>
          <span className="small-typography text-gray-600">
            Visualize os detalhes do contato e atribua um atendente.
          </span>
        </Col>
      </Row>

      <div className="bg-white rounded shadow p-4 mt-4">
        {isFetching ? (
          <Loader />
        ) : (
          <>
            <Row>
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label text-gray-600">Assunto</label>
                  <div className="fw-bold">{contact?.subject}</div>
                </div>
              </Col>
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label text-gray-600">Usuário</label>
                  <div className="fw-bold">{contact?.user?.email}</div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label text-gray-600">Data</label>
                  <div className="fw-bold">
                    {contact?.createdAt
                      ? moment(contact.createdAt).format('LLL')
                      : '-'}
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md="12">
                <div className="mb-3">
                  <label className="form-label text-gray-600">Mensagem</label>
                  <div className="border rounded p-3 bg-light">
                    {contact?.message}
                  </div>
                </div>
              </Col>
            </Row>

            <hr className="my-4" />

            <Row className="align-items-end">
              <Col md="6">
                <label className="form-label">Atendente</label>
                <AsyncTypeahead
                  options={users?.items || []}
                  delay={500}
                  onSearch={(query) => setUsersSearch(query)}
                  labelKey={'email'}
                  clearButton
                  placeholder="Pesquisar atendente"
                  isLoading={isLoadingUsers}
                  onChange={(selected) => {
                    if (selected && selected.length) {
                      const u = selected[0] as AdminUser;
                      setSelectedUserId(u.id);
                    } else {
                      setSelectedUserId(null);
                    }
                    setOpenUserPanel(false);
                  }}
                  onFocus={() => setOpenUserPanel(true)}
                  onBlur={() => setOpenUserPanel(false)}
                  onMenuToggle={setOpenUserPanel}
                  open={openUserPanel}
                  selected={selectedUser ? [selectedUser] : []}
                />
              </Col>
              <Col md="6" className="d-flex gap-2 justify-content-end">
                <Button
                  type="button"
                  color="secondary"
                  loading={isAssigning}
                  onClick={() => handleAssign({ atendenteId: null })}
                >
                  Atribuir a mim
                </Button>
                <Button
                  type="button"
                  color="primary"
                  loading={isAssigning}
                  disabled={!selectedUserId}
                  onClick={() =>
                    handleAssign({ atendenteId: selectedUserId || null })
                  }
                >
                  Atribuir
                </Button>
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSupportContactsFormPage;
